import type { DoneInvokeEvent, PropertyMapper } from 'xstate';
import { forwardTo, send, createMachine, assign } from 'xstate';
import * as Sentry from '@sentry/browser';
import { hotjar } from '@hotjar/tracking';
import { customerOrderApi, subscriptionApi } from '~/subscription-management/api';
import { cancelSubscription } from '~/subscription-management/api/order/cancel-subscription';
import { getSubscriptionInfo } from '~/subscription-management/api/subscription/get-subscription-info';
import type { SubscriptionInfo } from '~/subscription-management/api/subscription/model';
import {
  prettifiedStepNameByStateNameMap,
  trackCheckout,
} from '~/subscription-management/utils/tracking/track-checkout';
import type {
  OrderProcessResponse,
  OrderStatusResponse,
  UpdateCustomerDetailsRequest,
} from '../../api/order/model';
import type { PreviewCostsResponse } from '../../api/preview-cost-changes/model';
import type {
  Context,
  Event,
  UpdateBillingPeriodEvent,
  UpdateAllowancesEvent,
  UpdatePreviewedCostsEvent,
  SetupSubscriptionEvent,
  NextEvent,
  CompletedStep,
  UpdatePaymentDetailsEvent,
} from './checkout.machine.types';
import { draftOrderFetcherMachine } from './child-machines/draft-order-fetcher.machine';
import type { DraftOrderFetcherContext } from './child-machines/draft-order-fetcher.machine.types';
import { productsAllowanceFetcherMachine } from './child-machines/product-allowance-fetcher.machine';
import type { ProductAllowanceFetcherContext } from './child-machines/product-allowance-fetcher.machine.types';
import { getDefaultContext } from './utils/context';
import {
  resetProductAllowancesToInitial,
  updateInvoiceDetails,
  updateProductAllowances,
  setProductAllowancesToBasic,
} from './utils/events';
import { PricePreviewMachine } from './child-machines/preview-price.machine';
import type {
  PricePreviewContext,
  PricePreviewEvent,
} from './child-machines/preview-price.machine.types';
import { invalidateCacheOnSuccess } from './utils/actions/invalidate-cache-on-success';
import { trackStepIn, trackStepOut } from './utils/actions/track-steps';
import {
  orderHasOtherFailure,
  orderHasPaymentFailure,
  orderIsProcessing,
  orderIsProcessingTooLong,
  orderIsSuccessful,
  orderIsChurnAndSuccessful,
  customerHasExternalPayment,
} from './utils/guards/get-order-status.guard';
import { isReactivation, isSubscriptionOverdue } from './utils/guards/order-confirmation';
import {
  customerIsUnchanged,
  isAmendmentOrReactivation,
  shouldSkipCustomerUpdate,
} from './utils/guards/amendment';
import { clearDraftOrder, updateDraftOrder } from '~/subscription-management/draft-order';
import { getAccountDiscounts } from '../../utils/discounts';
import { navigateToExternalPage } from './utils/actions/navigate-to-external-page';
import { SUBSCRIPTIONS } from '../../constants/routes';
import { paymentDetailsMachine } from './child-machines/payment-details.machine';
import type { PaymentDetailsContext } from './child-machines/payment-details.machine.types';

export const checkoutMachine = createMachine(
  {
    // eslint-disable-next-line @typescript-eslint/consistent-type-imports
    tsTypes: {} as import('./checkout.machine.typegen').Typegen0,
    schema: {
      context: {} as Context,
      events: {} as Event,
      services: {} as {
        getSubscriptionInfo: { data: SubscriptionInfo };
        getOrderStatus: { data: OrderStatusResponse };
      },
    },
    preserveActionOrder: true,
    id: 'checkout',
    initial: 'initialise',
    states: {
      initialise: {
        initial: 'fetchSubscriptionInfo',
        states: {
          fetchSubscriptionInfo: {
            invoke: {
              id: 'getSubscriptionInfo',
              src: 'getSubscriptionInfo',
              onDone: [
                {
                  cond: 'customerHasExternalPayment',
                  target: 'navigateToSubscriptionsPage',
                },
                {
                  actions: [
                    assign<Context, DoneInvokeEvent<SubscriptionInfo>>((_context, event) => ({
                      ...getDefaultContext(event.data.customer, event.data.user),
                    })),
                    'updateCompletedSteps',
                  ],
                  target: 'checkOrderStatus',
                },
              ],
              onError: [
                {
                  actions: [
                    send<Context, DoneInvokeEvent<PreviewCostsResponse>>('NOTIFICATION'),
                    (_context, event) => {
                      Sentry.captureMessage(
                        `Error getting subscription info for Billing 3.0: ${event.data}`,
                      );
                      Sentry.captureException(event.data);
                    },
                  ],
                  target: 'checkOrderStatus',
                },
              ],
            },
          },
          navigateToSubscriptionsPage: { onEntry: [() => navigateToExternalPage(SUBSCRIPTIONS)] },
          checkOrderStatus: {
            invoke: {
              src: 'getOrderStatus',
              id: 'getOrderStatus',
              onDone: [
                {
                  cond: 'orderIsProcessingTooLong',
                  target: '#checkout.processOrder.donePending',
                },
                {
                  cond: 'orderIsProcessing',
                  target: '#checkout.processOrder.processing.pollStatus.polling',
                },
                {
                  cond: 'orderHasOtherFailure',
                  actions: assign<Context, DoneInvokeEvent<OrderStatusResponse>>(() => ({
                    hasOtherOrderFailure: true,
                    canProceed: false,
                  })),
                  target: 'fetchProducts',
                },
                {
                  target: 'fetchProducts',
                },
              ],
              onError: {
                target: 'fetchProducts',
              },
            },
          },
          fetchProducts: {
            invoke: {
              id: 'productsAllowanceFetcherMachine',
              src: productsAllowanceFetcherMachine,
              data: {
                accountId: (context) => context.accountId,
                hasActiveSubscription: (context) => context.hasActiveSubscription,
                allEffectiveAllowances: [],
                allowancesToAdd: [],
              } as PropertyMapper<Context, Event, ProductAllowanceFetcherContext>,
              onDone: [
                {
                  target: 'fetchDraftOrder',
                  actions: assign<Context, DoneInvokeEvent<ProductAllowanceFetcherContext>>({
                    allEffectiveAllowances: (_context, event) => event.data.allEffectiveAllowances,
                    allRenewedAllowances: (_context, event) => event.data.allRenewedAllowances,
                    allowancesToAdd: (_context, event) => event.data.allowancesToAdd,
                    isOnTrial: (_context, event) => event.data.isOnTrial,
                  }),
                },
              ],
            },
          },
          fetchDraftOrder: {
            invoke: {
              id: 'draftOrderFetcherMachine',
              src: draftOrderFetcherMachine,
              data: {
                allEffectiveAllowances: (context) => context.allEffectiveAllowances,
                allowancesToAdd: (context) => context.allowancesToAdd,
                allowancesToRemove: (context) => context.allowancesToRemove,
                hasActiveSubscription: (context) => context.hasActiveSubscription,
                billingPeriod: (context) => context.billingPeriod,
              } as PropertyMapper<Context, Event, DraftOrderFetcherContext>,
              onDone: {
                target: 'finish',
                actions: assign<Context, DoneInvokeEvent<DraftOrderFetcherContext>>({
                  allowancesToAdd: (_context, event) => event.data.allowancesToAdd,
                  allowancesToRemove: (_context, event) => event.data.allowancesToRemove,
                  loadedFromDraftOrder: (_context, event) => event.data.loadedFromDraftOrder,
                  billingPeriod: (_context, event) => event.data.billingPeriod,
                }),
              },
            },
          },
          finish: {
            always: [
              {
                target: '#checkout.confirmOrder.#checkout.confirmOrder.reactivation',
                cond: 'isReactivation',
              },
              {
                target: '#checkout.confirmOrder.#checkout.confirmOrder.overdue',
                cond: 'isSubscriptionOverdue',
              },
              { target: '#checkout.editOrder' },
            ],
          },
        },
        exit: ({
          accountId,
          customerId,
          hasActiveSubscription,
          loadedFromDraftOrder,
          isOnTrial,
          allEffectiveAllowances,
        }) => {
          trackCheckout('Checkout Started v3', {
            accountId,
            customerId,
            conversionType: hasActiveSubscription ? 'AMENDMENT' : 'FIRST_TIME',
            draftOrder: loadedFromDraftOrder,
            onTrial: isOnTrial,
            referrer: document.referrer,
            /**
             * NOTE: today we only have one initial step. if this ever changes we should stop hard coding it here
             */
            initialStep: prettifiedStepNameByStateNameMap['editOrder.steps.editSubscription'],
            initialPlans: allEffectiveAllowances?.map(({ sku, entity }) => ({
              sku,
              entity,
            })),
          });
        },
      },
      editOrder: {
        type: 'parallel',
        states: {
          previewCost: {
            invoke: {
              id: 'PricePreviewMachine',
              src: PricePreviewMachine,
              data: (context) => context,
            },
            on: {
              UPDATE_PREVIEWED_COSTS: {
                actions: assign<Context, UpdatePreviewedCostsEvent>({
                  costs: (_context, event) => event.costs,
                  canProceed: (_context, event) => event.canProceed,
                  isOnTrialWithBasicAllowances: (_context, event) =>
                    event.isOnTrialWithBasicAllowances,
                  accountDiscounts: ({ customer, subscription }, { costs }) =>
                    getAccountDiscounts(
                      costs.renewWith?.purchasedProductCosts ??
                        customer?.currentPeriodCostSummary?.purchasedProductCosts,
                      false,
                      subscription,
                    ),
                }),
              },
            },
          },
          steps: {
            initial: 'editSubscription',
            states: {
              editSubscription: {
                entry: ['trackStepIn', 'updateCompletedSteps'],
                exit: ['trackStepOut'],
                on: {
                  NEXT: [
                    {
                      cond: (_: Context, event: NextEvent) =>
                        event.isChurning !== undefined && event.isChurning,
                      actions: [
                        assign<Context, NextEvent>((context) => ({
                          ...setProductAllowancesToBasic(context),
                          isChurning: true,
                        })),
                        'previewPrice',
                      ],
                      target: 'reviewOrder',
                    },
                    {
                      cond: (context: Context) =>
                        context.completedSteps.includes('editOrder.steps.invoiceDetails') &&
                        context.completedSteps.includes('editOrder.steps.paymentDetails'),
                      target: 'reviewOrder',
                    },
                    {
                      cond: (context: Context) =>
                        context.completedSteps.includes('editOrder.steps.invoiceDetails'),
                      target: 'paymentDetails',
                    },

                    {
                      target: 'invoiceDetails',
                    },
                  ],
                  UPDATE_ALLOWANCES: {
                    actions: [
                      assign<Context, UpdateAllowancesEvent>(updateProductAllowances),
                      /**
                       * WARNING: There is an edge case using allowancesToAdd to update the draft order:
                       *
                       * if you are on trial and change the trial allowance to be basic
                       * on refresh, the list will pre-populate again with the trail allowance instead of basic.
                       *
                       * This is because we change the initial allowance to basic for trail allowances and now
                       * the selected basic is the same as the initial so it is not added to allowanceToAdd.
                       *
                       * Leaving it like this for now is not a train smash and avoids a much bigger refactor for very little benefit
                       * FIXME: (non-blocking): only fix this if there is clearly enough customers that gets confused by this behavior
                       */
                      ({ allowancesToAdd }: Context) =>
                        updateDraftOrder({
                          type: 'products',
                          payload: allowancesToAdd,
                        }),
                      'previewPrice',
                    ],
                  },
                  UPDATE_BILLING_PERIOD: {
                    actions: [
                      assign<Context, UpdateBillingPeriodEvent>({
                        billingPeriod: (_context, event) => event.billingPeriod,
                      }),
                      ({ billingPeriod }: Context) =>
                        updateDraftOrder({
                          type: 'billingPeriod',
                          payload: billingPeriod,
                        }),
                      'previewPrice',
                    ],
                  },
                },
              },
              invoiceDetails: {
                entry: ['trackStepIn', 'updateCompletedSteps'],
                exit: ['trackStepOut'],
                initial: 'edit',
                states: {
                  edit: {},
                  validate: {
                    after: {
                      200: [
                        {
                          cond: (context: Context) =>
                            context.completedSteps.includes('editOrder.steps.paymentDetails') &&
                            context.canProceed,
                          target: '#reviewOrder',
                        },
                        { cond: ({ canProceed }) => canProceed, target: '#paymentDetails' },
                        { target: 'validate' },
                      ],
                    },
                  },
                  updateCustomerDetails: {
                    invoke: {
                      id: 'updateCustomerDetails',
                      src: 'updateCustomerDetails',
                      onDone: [
                        {
                          actions: [
                            assign<Context, DoneInvokeEvent<Context['customer']>>(
                              (_, { data }) => ({ canProceed: false, customer: data }),
                            ),
                            'previewPrice',
                          ],
                          target: 'validate',
                        },
                      ],
                      onError: [{ target: 'updateError' }],
                    },
                  },
                  previewError: {
                    // Allow the user to try again by enabling the next button
                    entry: assign<Context, UpdatePreviewedCostsEvent>({ canProceed: true }),
                    on: {
                      NEXT: { actions: ['previewPrice'], target: 'validate' },
                    },
                  },
                  updateError: {
                    // Allow the user to try again by enabling the next button
                    entry: assign<Context, UpdatePreviewedCostsEvent>({ canProceed: true }),
                    on: {
                      // We allow the users to skip this step so that they are not blocked in the checkout flow.
                      NEXT: { cond: 'shouldSkipCustomerUpdate', target: '#reviewOrder' },
                    },
                  },
                },
                on: {
                  NEXT: [
                    { cond: 'customerIsUnchanged', target: '#reviewOrder' },
                    { cond: 'isAmendmentOrReactivation', target: '.updateCustomerDetails' },
                    {
                      actions: [assign<Context, NextEvent>(updateInvoiceDetails), 'previewPrice'],
                      target: '.validate',
                    },
                  ],
                  UPDATE_PREVIEWED_COSTS: {
                    // This happens when the PricePreviewMachine errors out
                    cond: (_: Context, event: UpdatePreviewedCostsEvent) =>
                      event?.canProceed === false,
                    target: '.previewError',
                  },
                  SETUP_SUBSCRIPTION: {
                    target: 'editSubscription',
                  },
                },
              },
              paymentDetails: {
                id: 'paymentDetails',
                entry: ['trackStepIn', 'updateCompletedSteps'],
                exit: ['trackStepOut'],
                invoke: {
                  id: 'paymentDetailsMachine',
                  src: paymentDetailsMachine,
                  data: {
                    accountId: (context) => context.accountId,
                    customer: (context) => context.customer,
                    isAmendmentOrReactivation: (context) => isAmendmentOrReactivation(context),
                  } as PropertyMapper<Context, Event, PaymentDetailsContext>,
                  onDone: {
                    actions: assign<Context, DoneInvokeEvent<PaymentDetailsContext>>({
                      customer: (_context, event) => event.data.customer,
                    }),
                    target: 'reviewOrder',
                  },
                },
                on: {
                  NEXT: {
                    target: 'reviewOrder',
                  },
                  SETUP_SUBSCRIPTION: {
                    target: 'editSubscription',
                  },
                  INVOICE_DETAILS: {
                    target: 'invoiceDetails',
                  },
                  UPDATE_PAYMENT_DETAILS: {
                    actions: forwardTo<Context, UpdatePaymentDetailsEvent>('paymentDetailsMachine'),
                  },
                },
              },
              reviewOrder: {
                id: 'reviewOrder',
                entry: ['trackStepIn', 'updateCompletedSteps'],
                exit: ['trackStepOut'],
                on: {
                  NEXT: {
                    target: '#checkout.processOrder',
                  },
                  SETUP_SUBSCRIPTION: [
                    {
                      cond: (_: Context, event: SetupSubscriptionEvent) => event.resetAllowances,
                      actions: [
                        assign<Context, SetupSubscriptionEvent>((context) => ({
                          ...resetProductAllowancesToInitial(context),
                          isChurning: false,
                        })),
                        'previewPrice',
                      ],
                      target: 'editSubscription',
                    },
                    {
                      target: 'editSubscription',
                    },
                  ],
                  INVOICE_DETAILS: {
                    target: 'invoiceDetails',
                  },
                  PAYMENT_DETAILS: {
                    target: 'paymentDetails',
                  },
                  CHURN: {
                    target: '#checkout.processOrder',
                  },
                },
              },
            },
          },
        },
      },
      confirmOrder: {
        initial: 'init',
        states: {
          init: {
            always: [
              { target: 'reactivation', cond: 'isReactivation' },
              { target: '#checkout.processOrder' },
            ],
          },
          /**
           * Confirming and doing reactivation separate to amendmends is an interim solution because at the time
           * of writing the backend still needs to solve allowing previews to costs against a cancelled subscriptoin
           * TODO: in future all reactivations should be treated as a normal amendmend
           */
          reactivation: {
            entry: ['trackStepIn'],
            exit: ['trackStepOut'],
            on: {
              NEXT: {
                target: '#checkout.processOrder',
              },
            },
          },
          overdue: {
            entry: ['trackStepIn'],
            exit: ['trackStepOut'],
            on: {
              NEXT: {
                actions: assign<Context, NextEvent>((context) => ({
                  ...resetProductAllowancesToInitial(context),
                  isChurning: true,
                })),
                target: '#checkout.processOrder',
              },
            },
          },
          // TODO: add churn flow in here as a separate machine instead of pop over modals
          churn: {},
        },
      },
      processOrder: {
        entry: ['clearDraftOrder'],
        initial: 'processing',
        states: {
          processing: {
            entry: ['trackStepIn'],
            exit: ['trackStepOut'],
            initial: 'process',
            states: {
              process: {
                invoke: {
                  src: (context) => {
                    if (context.isChurning) {
                      return cancelSubscription({ hotjarAccountId: context.accountId });
                    }
                    return context.processOrder(context);
                  },
                  id: 'processOrder',
                  onDone: [
                    {
                      actions: assign<Context, DoneInvokeEvent<OrderProcessResponse>>({
                        orderProcessed: true,
                      }),
                      target: 'pollStatus',
                    },
                  ],
                  onError: [
                    {
                      actions: assign<Context, DoneInvokeEvent<OrderStatusResponse>>(() => ({
                        hasPaymentOrderFailure: true,
                      })),
                      target: '#checkout.processOrder.doneError',
                    },
                  ],
                },
              },
              pollStatus: {
                initial: 'statusCheck',
                states: {
                  statusCheck: {
                    invoke: {
                      src: 'getOrderStatus',
                      id: 'getOrderStatus',
                      onDone: [
                        {
                          cond: 'orderIsChurnAndSuccessful',
                          actions: 'invalidateCacheOnSuccess',
                          target: '#checkout.processOrder.doneChurnSuccess',
                        },
                        {
                          cond: 'orderIsSuccessful',
                          actions: 'invalidateCacheOnSuccess',
                          target: '#checkout.processOrder.doneSuccess',
                        },
                        {
                          cond: 'orderHasPaymentFailure',
                          actions: assign<Context, DoneInvokeEvent<OrderStatusResponse>>(() => ({
                            hasPaymentOrderFailure: true,
                          })),
                          target: '#checkout.editOrder.steps.reviewOrder',
                        },
                        {
                          cond: 'orderHasOtherFailure',
                          target: '#checkout.processOrder.doneError',
                        },
                        {
                          cond: 'orderIsProcessingTooLong',
                          target: '#checkout.processOrder.donePending',
                        },
                        {
                          target: 'polling',
                        },
                      ],
                    },
                  },
                  polling: {
                    after: {
                      POLL_INTERVAL: {
                        target: 'statusCheck',
                      },
                    },
                  },
                },
              },
            },
          },
          doneSuccess: {
            entry: ['trackStepIn', 'showSurveyOnSuccess'],
            exit: ['trackStepOut'],
            type: 'final',
          },
          doneChurnSuccess: {
            entry: ['trackStepIn', 'showSurveyOnSuccess'],
            exit: ['trackStepOut'],
            type: 'final',
          },
          doneError: {
            entry: ['trackStepIn'],
            exit: ['trackStepOut'],
            type: 'final',
            on: {
              BACK: {
                target: '#checkout.editOrder.steps.reviewOrder',
              },
              RETRY: [
                {
                  cond: 'orderProcessed',
                  target: '#checkout.processOrder.processing.pollStatus',
                },
                {
                  target: '#checkout.processOrder.processing.process',
                },
              ],
            },
          },
          donePending: {
            entry: ['trackStepIn'],
            exit: ['trackStepOut'],
            type: 'final',
          },
        },
      },
    },
  },
  {
    delays: { POLL_INTERVAL: 2000 },
    services: {
      getOrderStatus: (context) => customerOrderApi.getOrderStatus(context.accountId),
      getSubscriptionInfo,
      updateCustomerDetails: (context, event: NextEvent): Promise<Context['customer']> => {
        const newCustomer = updateInvoiceDetails(context, event).customer;
        return subscriptionApi
          .updateCustomerDetails({
            ...newCustomer,
            hotjarAccountId: context.accountId,
            // Typecasting this for now, this should be fixed when we start using insights-api-types everywhere
          } as UpdateCustomerDetailsRequest)
          .then(() => newCustomer);
      },
    },
    guards: {
      orderIsSuccessful,
      orderIsChurnAndSuccessful,
      orderHasPaymentFailure,
      orderHasOtherFailure,
      orderIsProcessing,
      orderIsProcessingTooLong,
      isReactivation,
      orderProcessed: (context) => context.orderProcessed,
      isAmendmentOrReactivation,
      shouldSkipCustomerUpdate,
      customerIsUnchanged,
      isSubscriptionOverdue,
      customerHasExternalPayment,
    },
    actions: {
      previewPrice: send(
        (context) => {
          return {
            type: 'PREVIEW_PRICE',
            accountId: context.accountId,
            customerId: context.customerId,
            allowancesToAdd: context.allowancesToAdd,
            allowancesToRemove: context.allowancesToRemove,
            billingPeriod: context.billingPeriod,
            customer: context.customer,
            currentPeriodCostSummary: context.currentPeriodCostSummary,
            isChurning: context.isChurning,
            getCostPreview: context.getCostPreview,
          } as PricePreviewContext & { type: PricePreviewEvent['type'] };
        },
        {
          to: 'PricePreviewMachine',
        },
      ),
      invalidateCacheOnSuccess,
      trackStepIn: ({ accountId, customerId }, _, { state }) => {
        trackStepIn({
          accountId,
          customerId,
          state: state.toStrings() ?? [],
          stateHistory: state.history?.toStrings() ?? [],
        });
      },
      trackStepOut: ({ accountId, customerId }, _, { state }) => {
        trackStepOut({
          accountId,
          customerId,
          state: state.toStrings() ?? [],
          stateHistory: state.history?.toStrings() ?? [],
        });
      },
      clearDraftOrder,
      showSurveyOnSuccess: () => hotjar.event('checkout_confirm'),
      updateCompletedSteps: assign((context: Context) => {
        const { allowancesToAdd, allowancesToRemove } = context;
        const { billingAddress, paymentMethod, paymentMethodExternalId } = context.customer;
        const hasBillingAddress =
          (billingAddress?.addressLine1 !== undefined && billingAddress?.addressLine1 !== null) ||
          (billingAddress?.unstructuredAddress !== undefined &&
            billingAddress?.unstructuredAddress !== null);

        const hasPaymentMethod =
          (paymentMethod !== undefined && paymentMethod !== null) ||
          (paymentMethodExternalId !== undefined && paymentMethodExternalId !== null);

        return {
          completedSteps: [
            ...(allowancesToAdd.length || allowancesToRemove.length
              ? ['editOrder.steps.editSubscription']
              : []),
            ...(hasBillingAddress ? ['editOrder.steps.invoiceDetails'] : []),
            ...(hasPaymentMethod ? ['editOrder.steps.paymentDetails'] : []),
          ] as CompletedStep[],
        };
      }),
    },
  },
);
